import { z } from "zod";
import { camadasSchema } from "./camadas.js";
import { CONEXOES_DE_IMPRESSORA, ESTADOS_DO_TRABALHO_DE_IMPRESSAO, } from "./enums.js";
/**
 * A folha de impressão: o postal da SELPHY CP1500 e o que cabe nele.
 *
 * Mora aqui, e não na API nem na tela, porque a mesma geometria decide duas
 * coisas que precisam coincidir ao pixel: o que o operador vê na prévia e o que
 * o servidor monta no JPEG. Duas implementações divergiriam em silêncio — a foto
 * sairia no papel num lugar diferente do que estava na tela.
 *
 * Tudo aqui é puro (sem I/O, sem DOM), por isso é testável no Vitest.
 */
/** Postal 100 × 148 mm a 300 dpi: o tamanho que a impressora aceita sem escala. */
export const FOLHA = {
    larguraMm: 100,
    alturaMm: 148,
    dpi: 300,
    larguraPx: 1181,
    alturaPx: 1748,
};
export const mmParaPx = (mm) => Math.round((mm / 25.4) * FOLHA.dpi);
/**
 * O que o papel realmente imprime e mantém: **93,6 × 141 mm**, no meio do postal.
 *
 * O postal tem 100 × 148 mm, mas as bordas (3,2 mm nas laterais, 3,5 mm em cima e embaixo)
 * ficam no picotado, e o que cai ali se perde. Medida do usuário, imprimindo a folha de
 * marcadores: as faixas das pontas perderam as laterais e o rodapé caiu na aba. Tudo que
 * não é sangria (a foto cheia é, por natureza) tem de caber nesta área.
 */
export const AREA_UTIL = { larguraMm: 93.6, alturaMm: 141 };
/** A área útil em pixels da folha, centrada: arredondada para dentro, nunca para fora. */
export function areaUtilEmPx() {
    const largura = Math.floor((AREA_UTIL.larguraMm / 25.4) * FOLHA.dpi);
    const altura = Math.floor((AREA_UTIL.alturaMm / 25.4) * FOLHA.dpi);
    return {
        x: Math.round((FOLHA.larguraPx - largura) / 2),
        y: Math.round((FOLHA.alturaPx - altura) / 2),
        largura,
        altura,
    };
}
// ─── Formatos ─────────────────────────────────────────────────────────────────
export const LAYOUT_IDS = [
    "cheia",
    "quatro",
    "nove-3x4",
    "tres-3x4-e-marcadores",
    "duas-molduras-e-marcador",
];
/**
 * O que um trabalho de impressão imprime: um dos formatos da folha montada na tela de Impressão, ou a folha
 * de **marcadores** (três faixas, já montada pelo servidor no conjunto). O formato só rotula o trabalho na fila;
 * o arquivo que vai à impressora é sempre o mesmo JPEG de 1181 × 1748 px.
 */
export const FORMATOS_DE_TRABALHO = [...LAYOUT_IDS, "marcadores"];
/**
 * O que vai num espaço da folha: uma foto da bandeja, ou a faixa de um marcador já gerado em /marcadores (#115).
 */
export const TIPOS_DE_ESPACO = ["foto", "marcador"];
/** Grade de células iguais, da esquerda para a direita e de cima para baixo. */
function grade(colunas, linhas, celula, vao, x0, y0) {
    const celulas = [];
    for (let l = 0; l < linhas; l++) {
        for (let c = 0; c < colunas; c++) {
            celulas.push({
                x: x0 + c * (celula.largura + vao),
                y: y0 + l * (celula.altura + vao),
                largura: celula.largura,
                altura: celula.altura,
            });
        }
    }
    return celulas;
}
/**
 * Guias no meio de cada vão, de um pouco antes a um pouco depois da grade: dão
 * por onde cortar sem invadir as fotos.
 */
function guiasDaGrade(colunas, linhas, celula, vao, x0, y0) {
    const sobra = 2;
    // A guia que passa da grade ajuda a alinhar o corte, mas nunca invade a margem do picotado.
    const xMin = (100 - AREA_UTIL.larguraMm) / 2;
    const yMin = (148 - AREA_UTIL.alturaMm) / 2;
    const noX = (x) => Math.min(Math.max(x, xMin), 100 - xMin);
    const noY = (y) => Math.min(Math.max(y, yMin), 148 - yMin);
    const larguraTotal = colunas * celula.largura + (colunas - 1) * vao;
    const alturaTotal = linhas * celula.altura + (linhas - 1) * vao;
    const guias = [];
    for (let c = 1; c < colunas; c++) {
        const x = x0 + c * (celula.largura + vao) - vao / 2;
        guias.push({ x1: x, y1: noY(y0 - sobra), x2: x, y2: noY(y0 + alturaTotal + sobra) });
    }
    for (let l = 1; l < linhas; l++) {
        const y = y0 + l * (celula.altura + vao) - vao / 2;
        guias.push({ x1: noX(x0 - sobra), y1: y, x2: noX(x0 + larguraTotal + sobra), y2: y });
    }
    return guias;
}
/**
 * Linhas de corte nas **bordas** de cada célula: cortando nelas, cada pedaço sai com o tamanho exato da foto, sem
 * borda branca. Uma vertical em cada borda de coluna (as fotos se encostam na largura) e duas horizontais por linha
 * de fotos, em cima e embaixo (entre as linhas fica a faixa do nome). Passam um pouco da grade, para alinhar a régua,
 * sem invadir o picotado.
 */
function guiasDasBordas(celulas, limites = {}) {
    const sobra = 2;
    const xMin = (100 - AREA_UTIL.larguraMm) / 2;
    const yMin = (148 - AREA_UTIL.alturaMm) / 2;
    // `xMax`: onde começa o marcador, numa folha mista — a guia da foto não risca a arte dele.
    const noX = (x) => Math.min(Math.max(x, xMin), limites.xMax ?? 100 - xMin);
    const noY = (y) => Math.min(Math.max(y, yMin), 148 - yMin);
    const unicos = (ns) => [...new Set(ns)].sort((a, b) => a - b);
    const xs = unicos(celulas.flatMap((c) => [c.x, c.x + c.largura]));
    const ys = unicos(celulas.flatMap((c) => [c.y, c.y + c.altura]));
    const [xIni, xFim] = [Math.min(...xs), Math.max(...xs)];
    const [yIni, yFim] = [Math.min(...ys), Math.max(...ys)];
    return [
        ...xs.map((x) => ({ x1: x, y1: noY(yIni - sobra), x2: x, y2: noY(yFim + sobra) })),
        ...ys.map((y) => ({ x1: noX(xIni - sobra), y1: y, x2: noX(xFim + sobra), y2: y })),
    ];
}
/**
 * As fotos da folha de 4 vão para molduras impressas em 3D, cujo encaixe tem no máximo 4,3 × 6,7 cm (#113): por isso
 * elas têm esse tamanho exato e o corte é nas bordas — o pedaço cortado é a foto, sem borda branca que o faria passar
 * do encaixe. Na largura, encostadas; embaixo de cada uma, uma faixa de 3 mm com o nome da pessoa, que fica fora do
 * corte e ajuda a saber de quem é cada foto.
 *
 * Duas linhas de 67 + 3 mm são 140 mm, nos 141 da área útil: a grade começa a 4 mm do topo (a área útil, a 3,5) e
 * termina a 144 (a área útil, a 144,5). Na largura, 86 mm centrados, de 7 a 93.
 */
const QUATRO = { largura: 43, altura: 67 };
const FAIXA_DO_NOME_MM = 3;
const X0_DO_QUATRO = (100 - 2 * QUATRO.largura) / 2;
const Y0_DO_QUATRO = 4;
const CELULAS_DO_QUATRO = [0, 1].flatMap((linha) => [0, 1].map((coluna) => ({
    x: X0_DO_QUATRO + coluna * QUATRO.largura,
    y: Y0_DO_QUATRO + linha * (QUATRO.altura + FAIXA_DO_NOME_MM),
    largura: QUATRO.largura,
    altura: QUATRO.altura,
})));
// ─── As folhas mistas: fotos e marcadores (#115) ─────────────────────────────
/**
 * O marcador na folha mista: a faixa de 31,2 × 141 mm, a mesma da folha de marcadores — um terço da largura útil e a
 * altura útil inteira. Duas faixas e a coluna de fotos de 3×4 somam os 93,6 mm da largura útil; uma faixa e a coluna
 * das molduras (43 mm) somam 74,2.
 */
const MARCADOR = { largura: AREA_UTIL.larguraMm / 3, altura: AREA_UTIL.alturaMm };
const X_DA_AREA_UTIL = (100 - AREA_UTIL.larguraMm) / 2;
const Y_DA_AREA_UTIL = (148 - AREA_UTIL.alturaMm) / 2;
/** O marcador que fica na coluna `n` (0, 1 ou 2) da área útil, como na folha de marcadores. */
const marcadorNaColuna = (n) => ({
    x: X_DA_AREA_UTIL + n * MARCADOR.largura,
    y: Y_DA_AREA_UTIL,
    largura: MARCADOR.largura,
    altura: MARCADOR.altura,
});
/** As fotos uma embaixo da outra, cada uma com a faixa do nome, encostadas à direita em `xDireita`. */
function colunaDeFotos(foto, quantas, xDireita, y0) {
    return Array.from({ length: quantas }, (_, i) => ({
        x: xDireita - foto.largura,
        y: y0 + i * (foto.altura + FAIXA_DO_NOME_MM),
        largura: foto.largura,
        altura: foto.altura,
    }));
}
/**
 * As guias de uma folha mista: as das fotos param na borda do marcador, e cada borda de marcador que não é a da área
 * útil (onde o picotado já corta) ganha uma linha da altura útil inteira — a borda entre a coluna de fotos e o
 * marcador é um corte só.
 */
function guiasDaFolhaMista(fotos, marcadores) {
    const bordaDoMarcador = Math.min(...marcadores.map((m) => m.x));
    const xsDosMarcadores = [...new Set(marcadores.flatMap((m) => [m.x, m.x + m.largura]))].filter((x) => x > X_DA_AREA_UTIL + 1e-9 && x < X_DA_AREA_UTIL + AREA_UTIL.larguraMm - 1e-9);
    const dasFotos = guiasDasBordas(fotos, { xMax: bordaDoMarcador }).filter((g) => !(g.x1 === g.x2 && xsDosMarcadores.some((x) => Math.abs(x - g.x1) < 1e-9)));
    return [
        ...dasFotos,
        ...xsDosMarcadores.map((x) => ({
            x1: x,
            y1: Y_DA_AREA_UTIL,
            x2: x,
            y2: Y_DA_AREA_UTIL + AREA_UTIL.alturaMm,
        })),
    ];
}
const faixasDoNome = (fotos) => fotos.map((c) => ({ x: c.x, y: c.y + c.altura, largura: c.largura, altura: FAIXA_DO_NOME_MM }));
/** 3 fotos 3×4 de 30 × 40, com o nome, na coluna da esquerda; o bloco de 129 mm centrado na altura útil. */
const FOTOS_DO_TRES = colunaDeFotos({ largura: 30, altura: 40 }, 3, X_DA_AREA_UTIL + MARCADOR.largura, Y_DA_AREA_UTIL + (AREA_UTIL.alturaMm - 3 * (40 + FAIXA_DO_NOME_MM)) / 2);
const MARCADORES_DO_TRES = [marcadorNaColuna(1), marcadorNaColuna(2)];
/** 2 fotos das molduras (43 × 67, como na folha de 4), com o nome, encostadas no marcador da direita. */
const FOTOS_DO_DUAS = colunaDeFotos(QUATRO, 2, X_DA_AREA_UTIL + 2 * MARCADOR.largura, Y0_DO_QUATRO);
const MARCADORES_DO_DUAS = [marcadorNaColuna(2)];
const NOVE = { largura: 30, altura: 40 };
// Três fotos de 30 mm e dois vãos têm de caber nos 93,6 mm da área útil (AREA_UTIL): com
// 3 mm de vão eram 96 mm, e a coluna das pontas perdia 1,2 mm de cada lado no picotado.
const VAO_DO_NOVE = 1.5;
export const LAYOUTS = {
    cheia: {
        id: "cheia",
        nome: "Foto cheia",
        descricao: "Uma foto na folha inteira, 100 × 148 mm.",
        celulas: [{ x: 0, y: 0, largura: FOLHA.larguraMm, altura: FOLHA.alturaMm }],
        guias: [],
    },
    quatro: {
        id: "quatro",
        nome: "4 por folha",
        descricao: "Quatro fotos de 43 × 67 mm, com o nome embaixo de cada uma, fora do corte.",
        celulas: CELULAS_DO_QUATRO,
        guias: guiasDasBordas(CELULAS_DO_QUATRO),
        legendas: CELULAS_DO_QUATRO.map((c) => ({
            x: c.x,
            y: c.y + c.altura,
            largura: c.largura,
            altura: FAIXA_DO_NOME_MM,
        })),
    },
    "nove-3x4": {
        id: "nove-3x4",
        nome: "9 fotos 3 × 4 cm",
        descricao: "Nove fotos de 30 × 40 mm, com guias de corte.",
        // 93 × 123 mm de grade, centrada na folha e dentro da área útil.
        celulas: grade(3, 3, NOVE, VAO_DO_NOVE, 3.5, 12.5),
        guias: guiasDaGrade(3, 3, NOVE, VAO_DO_NOVE, 3.5, 12.5),
    },
    "tres-3x4-e-marcadores": {
        id: "tres-3x4-e-marcadores",
        nome: "3 fotos 3 × 4 + 2 marcadores",
        descricao: "Três fotos de 30 × 40 mm, com o nome, e dois marcadores prontos.",
        celulas: [...FOTOS_DO_TRES, ...MARCADORES_DO_TRES],
        tipos: ["foto", "foto", "foto", "marcador", "marcador"],
        guias: guiasDaFolhaMista(FOTOS_DO_TRES, MARCADORES_DO_TRES),
        legendas: faixasDoNome(FOTOS_DO_TRES),
    },
    "duas-molduras-e-marcador": {
        id: "duas-molduras-e-marcador",
        nome: "2 fotos (molduras) + 1 marcador",
        descricao: "Duas fotos de 43 × 67 mm, com o nome, e um marcador pronto.",
        celulas: [...FOTOS_DO_DUAS, ...MARCADORES_DO_DUAS],
        tipos: ["foto", "foto", "marcador"],
        guias: guiasDaFolhaMista(FOTOS_DO_DUAS, MARCADORES_DO_DUAS),
        legendas: faixasDoNome(FOTOS_DO_DUAS),
    },
};
/** O tipo do espaço `indice` do formato: foto, a não ser onde o formato diz marcador. */
export function tipoDoEspaco(layout, indice) {
    return LAYOUTS[layout].tipos?.[indice] ?? "foto";
}
/** O formato tem espaço de marcador? */
export function temEspacoDeMarcador(layout) {
    return (LAYOUTS[layout].tipos ?? []).includes("marcador");
}
export const LISTA_DE_LAYOUTS = LAYOUT_IDS.map((id) => LAYOUTS[id]);
/** Seis casas bastam (um milionésimo da célula) e tiram o ruído de ponto flutuante das contas em mm. */
const seisCasas = (n) => Math.round(n * 1_000_000) / 1_000_000;
/**
 * A área útil do papel (`AREA_UTIL`) vista de dentro de uma célula, em frações da célula: o que dela o papel
 * imprime e mantém. Fora desse retângulo, a foto cai no picotado e se perde.
 *
 * É `null` quando **não há o que marcar**: a célula inteira cabe na área útil (nada é cortado) ou nem a toca. A
 * tela usa isto para desenhar a linha no editor de enquadramento só onde o corte existe — na prática, a foto
 * cheia, que é sangria de propósito.
 */
export function areaUtilDentroDe(celula) {
    const xMin = (FOLHA.larguraMm - AREA_UTIL.larguraMm) / 2;
    const yMin = (FOLHA.alturaMm - AREA_UTIL.alturaMm) / 2;
    const x0 = Math.max(celula.x, xMin);
    const y0 = Math.max(celula.y, yMin);
    const x1 = Math.min(celula.x + celula.largura, xMin + AREA_UTIL.larguraMm);
    const y1 = Math.min(celula.y + celula.altura, yMin + AREA_UTIL.alturaMm);
    if (x1 <= x0 || y1 <= y0)
        return null;
    const area = {
        x: seisCasas((x0 - celula.x) / celula.largura),
        y: seisCasas((y0 - celula.y) / celula.altura),
        largura: seisCasas((x1 - x0) / celula.largura),
        altura: seisCasas((y1 - y0) / celula.altura),
    };
    // A célula inteira dentro da área útil: a "área" seria a própria célula, e não há corte a mostrar.
    return area.x === 0 && area.y === 0 && area.largura === 1 && area.altura === 1 ? null : area;
}
/** `areaUtilDentroDe` para a célula `indice` de um formato; `null` se a célula não existe. */
export function areaUtilNaCelula(layout, indice) {
    const celula = LAYOUTS[layout].celulas[indice];
    return celula ? areaUtilDentroDe(celula) : null;
}
/** As células de um formato em pixels da folha (300 dpi). */
export function celulasEmPx(layout) {
    return LAYOUTS[layout].celulas.map((c) => ({
        x: mmParaPx(c.x),
        y: mmParaPx(c.y),
        largura: mmParaPx(c.largura),
        altura: mmParaPx(c.altura),
    }));
}
/**
 * O enquadramento de partida, centrado: "preencher" cobre a célula inteira e
 * corta o que sobra; "encaixar" mostra a foto inteira e deixa a faixa branca.
 */
export function enquadramentoInicial(foto, celula, modo) {
    const escalaX = celula.largura / foto.largura;
    const escalaY = celula.altura / foto.altura;
    const escala = modo === "preencher" ? Math.max(escalaX, escalaY) : Math.min(escalaX, escalaY);
    const largura = (celula.largura / escala / foto.largura) * 100;
    const altura = (celula.altura / escala / foto.altura) * 100;
    return { x: (100 - largura) / 2, y: (100 - altura) / 2, largura };
}
const limitar = (v, min, max) => Math.min(Math.max(v, min), max);
/**
 * O que o servidor faz com uma célula: recorta `extrair` da foto, redimensiona
 * para `destino` e põe sobre o fundo branco da célula.
 *
 * `null` quando a área visível não toca a foto (a célula fica em branco).
 * A mesma escala vale nos dois eixos, então nada é esticado.
 */
export function planoDaCelula(enquadramento, foto, celula) {
    const larguraVisivel = (enquadramento.largura / 100) * foto.largura;
    if (!(larguraVisivel > 0))
        return null;
    const escala = celula.largura / larguraVisivel; // pixels da célula por pixel da foto
    const alturaVisivel = celula.altura / escala;
    const rx = (enquadramento.x / 100) * foto.largura;
    const ry = (enquadramento.y / 100) * foto.altura;
    const x0 = Math.max(rx, 0);
    const y0 = Math.max(ry, 0);
    const x1 = Math.min(rx + larguraVisivel, foto.largura);
    const y1 = Math.min(ry + alturaVisivel, foto.altura);
    if (x1 <= x0 || y1 <= y0)
        return null;
    const left = limitar(Math.round(x0), 0, foto.largura - 1);
    const top = limitar(Math.round(y0), 0, foto.altura - 1);
    const destinoX = limitar(Math.round((x0 - rx) * escala), 0, celula.largura - 1);
    const destinoY = limitar(Math.round((y0 - ry) * escala), 0, celula.altura - 1);
    return {
        extrair: {
            left,
            top,
            largura: limitar(Math.round(x1) - left, 1, foto.largura - left),
            altura: limitar(Math.round(y1) - top, 1, foto.altura - top),
        },
        destino: {
            x: destinoX,
            y: destinoY,
            largura: limitar(Math.round((x1 - x0) * escala), 1, celula.largura - destinoX),
            altura: limitar(Math.round((y1 - y0) * escala), 1, celula.altura - destinoY),
        },
    };
}
/**
 * Onde a foto inteira fica dentro da caixa da célula, em % da caixa — para a
 * prévia desenhar a foto com `position: absolute` e a célula com
 * `overflow: hidden`. É a mesma conta de `planoDaCelula`, escrita para CSS.
 * `celula` pode vir em qualquer unidade: só a proporção entra.
 */
export function estiloDaImagem(enquadramento, foto, celula) {
    const larguraVisivel = (enquadramento.largura / 100) * foto.largura;
    const escala = celula.largura / larguraVisivel;
    const topoEmCelula = -((enquadramento.y / 100) * foto.altura) * escala;
    return {
        left: (-enquadramento.x / enquadramento.largura) * 100,
        top: (topoEmCelula / celula.altura) * 100,
        width: 10000 / enquadramento.largura,
        height: ((foto.altura * escala) / celula.altura) * 100,
    };
}
// ─── O pedido ao servidor ─────────────────────────────────────────────────────
/**
 * Os limites cobrem o zoom de 10× para dentro e de 20× para fora: o que passa
 * disso é um enquadramento sem foto à vista, e não vale uma folha.
 */
export const enquadramentoSchema = z.object({
    x: z.number().finite().min(-2000).max(2000),
    y: z.number().finite().min(-2000).max(2000),
    largura: z.number().finite().min(10).max(2000),
});
/**
 * De onde sai a imagem de uma foto: a **peça** publicada (a capa da vivência nos 40%
 * de cima mais o retrato, ou, sem capa, a própria foto), a **foto** que a IA gerou,
 * sem capa, ou a foto **original**, a que o operador tirou e da qual a IA partiu.
 *
 * Só as fotos compostas têm a foto da IA à parte; nas outras a peça já é a foto. A
 * original existe à parte em toda foto feita com IA; na foto salva sem IA (#95) a peça
 * já é a original.
 */
export const FONTES_DA_FOTO = ["peca", "foto", "original"];
export const celulaDeFotoSchema = z.object({
    tipo: z.literal("foto").optional(),
    geracaoId: z.number().int().positive(),
    /**
     * A ampliação de cenário a usar no lugar da foto publicada (`imagens_de_impressao`).
     * Tem de ser do mesmo dono e da mesma foto.
     */
    imagemId: z.number().int().positive().optional(),
    /** A versão da foto; sem ela, a peça publicada. Vale quando não há `imagemId`. */
    fonte: z.enum(FONTES_DA_FOTO).optional(),
    /** Sem enquadramento, o servidor preenche a célula (corta o que sobra). */
    enquadramento: enquadramentoSchema.optional(),
    /**
     * As figurinhas, logos e textos desta foto, de baixo para cima. As de imagem citam a biblioteca por id:
     * o servidor confere que o dono pode usá-las e as lê do storage (`montarFolhaDoPedido`).
     */
    camadas: camadasSchema.optional(),
});
/**
 * Um espaço de marcador (#115): a faixa `faixa` (0, 1 ou 2 — são três por conjunto) de um marcador já gerado. O
 * servidor confere que o marcador é do dono e que a faixa existe.
 */
export const celulaDeMarcadorSchema = z.object({
    tipo: z.literal("marcador"),
    marcadorId: z.number().int().positive(),
    faixa: z.number().int().min(0).max(2),
});
export const celulaDaFolhaSchema = z.union([celulaDeMarcadorSchema, celulaDeFotoSchema]);
/** A célula é de marcador? (A de foto pode vir sem `tipo`: é o pedido de antes das folhas mistas.) */
export function ehCelulaDeMarcador(c) {
    return c.tipo === "marcador";
}
const folhaBase = z.object({
    layout: z.enum(LAYOUT_IDS),
    /** Uma entrada por célula, na ordem do formato; `null` deixa a célula em branco. */
    celulas: z.array(celulaDaFolhaSchema.nullable()).max(9),
});
/**
 * O teto de camadas de um pedido inteiro, além das 12 de cada célula. Sem ele, 9 células com 12 camadas
 * cada decodificam, redimensionam e giram até 108 PNGs de 2048 px num pedido só — e o servidor imprime com
 * uma fila de um. 60 deixam folgado o uso real (um título e duas figurinhas em cada uma das 9 fotos).
 */
export const MAX_CAMADAS_POR_FOLHA = 60;
/** O teto de figurinhas **diferentes** de um pedido: cada uma é lida do storage e decodificada uma vez. */
export const MAX_FIGURINHAS_DISTINTAS_POR_FOLHA = 24;
/** As regras que valem para qualquer pedido que leve uma folha — a do arquivo e a de imprimir. */
function regrasDaFolha(folha, ctx) {
    if (folha.celulas.length > LAYOUTS[folha.layout].celulas.length) {
        ctx.addIssue({ code: "custom", message: "Há mais fotos do que células neste formato" });
    }
    if (!folha.celulas.some((c) => c !== null)) {
        ctx.addIssue({
            code: "custom",
            message: temEspacoDeMarcador(folha.layout)
                ? "Escolha ao menos uma foto ou marcador"
                : "Escolha ao menos uma foto",
        });
    }
    // Cada coisa no espaço que é dela: a faixa de marcador não cabe no espaço da foto, e vice-versa.
    folha.celulas.forEach((c, i) => {
        if (!c)
            return;
        const esperado = tipoDoEspaco(folha.layout, i);
        const veio = ehCelulaDeMarcador(c) ? "marcador" : "foto";
        if (veio !== esperado) {
            ctx.addIssue({
                code: "custom",
                message: `O espaço ${i + 1} é de ${esperado}, e veio um ${veio}`,
            });
        }
    });
    const camadas = folha.celulas.flatMap((c) => c && !ehCelulaDeMarcador(c) ? (c.camadas ?? []) : []);
    if (camadas.length > MAX_CAMADAS_POR_FOLHA) {
        ctx.addIssue({
            code: "custom",
            message: `A folha tem camadas demais (${camadas.length}): o máximo é ${MAX_CAMADAS_POR_FOLHA}`,
        });
    }
    const figurinhas = new Set(camadas.flatMap((k) => (k.tipo === "imagem" ? [k.figurinhaId] : [])));
    if (figurinhas.size > MAX_FIGURINHAS_DISTINTAS_POR_FOLHA) {
        ctx.addIssue({
            code: "custom",
            message: `A folha usa figurinhas diferentes demais (${figurinhas.size}): o máximo é ${MAX_FIGURINHAS_DISTINTAS_POR_FOLHA}`,
        });
    }
}
export const folhaDeImpressaoSchema = folhaBase.superRefine(regrasDaFolha);
// ─── Imprimir direto, por IPP ─────────────────────────────────────────────────
/**
 * Cópias por pedido. Cada cópia é um trabalho IPP à parte (o atributo `copies` fica de fora: ele estava no
 * trabalho que travou a fila), então o teto também limita quanto papel e fita um toque desperdiça.
 */
export const COPIAS_MAXIMAS = 5;
/**
 * Imprimir: a folha do pedido de sempre, mais onde e quantas. Sem `dono` aqui — quem é o dono do trabalho
 * sai da impressora, que o servidor confere contra a sessão.
 */
export const imprimirFolhaSchema = folhaBase
    .extend({
    impressoraId: z.number().int().positive(),
    copias: z.number().int().min(1).max(COPIAS_MAXIMAS),
})
    .superRefine(regrasDaFolha);
/**
 * Cadastrar ou editar uma impressora (só o admin). O endereço é conferido no servidor, que é quem faz a
 * requisição a ele: aqui só o tamanho. Pelo agente (#109), não há endereço: quem acha a impressora na rede
 * do evento é o agente.
 */
export const salvarImpressoraSchema = z
    .object({
    /** Com `id`, edita; sem, cadastra. */
    id: z.number().int().positive().optional(),
    nome: z.string().trim().min(2, "Dê um nome à impressora").max(60),
    /** Sem ela, `direta`: o cadastro de antes do agente continua valendo como era. */
    conexao: z.enum(CONEXOES_DE_IMPRESSORA).default("direta"),
    /** `ipp://<ip ou nome>:631/ipp/print`, só na `direta`. O servidor grava a forma normalizada. */
    ippUri: z.string().trim().max(300).optional(),
    /** De qual cliente é a impressora. Sem ele, é do próprio admin. Só vale ao cadastrar. */
    donoId: z.number().int().positive().optional(),
})
    .superRefine((v, ctx) => {
    if (v.conexao === "direta" && !v.ippUri) {
        ctx.addIssue({
            code: "custom",
            path: ["ippUri"],
            message: "Informe o endereço da impressora",
        });
    }
});
/**
 * O estado da impressora como o agente o manda ao servidor: o mesmo `EstadoGravadoDaImpressora`, conferido.
 * Vem de fora (do notebook do evento), então tudo tem teto.
 */
export const estadoGravadoDaImpressoraSchema = z.union([
    z.object({
        alcancavel: z.literal(true),
        estado: z.enum(["idle", "processing", "stopped"]),
        motivos: z.array(z.string().max(100)).max(30),
        filaDaImpressora: z.number().int().min(0).max(10_000),
        papel: z.array(z.string().max(100)).max(30),
        aceitandoTrabalhos: z.boolean(),
        nome: z.string().max(200).nullable(),
    }),
    z.object({ alcancavel: z.literal(false), mensagem: z.string().max(500) }),
]);
// ─── O agente de impressão (#109) ─────────────────────────────────────────────
/** Quanto vale o código que liga um agente a uma impressora. */
export const VALIDADE_DO_CODIGO_DO_AGENTE_MIN = 10;
/** Sem dar sinal por esse tempo, o agente conta como desligado: ele pergunta a cada 3 s. */
export const AGENTE_CONECTADO_SE_VISTO_HA_MS = 15_000;
/** Quanto uma folha espera o agente pegá-la antes de falhar (decisão do usuário, 16/10/2026). */
export const ESPERA_PELO_AGENTE_MIN = 10;
/** A impressora como o agente a achou na rede do evento, pelo mDNS. */
export const impressoraNaRedeSchema = z.object({
    /** O nome que ela anuncia ("Canon SELPHY CP1500"). */
    nome: z.string().trim().max(200),
    /** O nome mDNS, sem o ponto final (`CP15006ce20a.local`): não muda de rede para rede. */
    host: z.string().trim().min(1).max(253),
    /** O IPv4 do anúncio mais recente: é ele que muda a cada rede. */
    endereco: z.string().trim().max(64),
    porta: z.number().int().min(1).max(65535),
    /** O caminho IPP (`/ipp/print`). */
    caminho: z.string().trim().max(200),
    modelo: z.string().trim().max(200).nullable(),
});
/** O agente se liga a uma impressora com o código que o admin gerou. */
export const parearAgenteSchema = z.object({
    codigo: z.string().trim().min(1).max(20),
    /** "notebook-da-sala (Windows)": para a tela dizer onde o agente está. */
    maquina: z.string().trim().max(120),
    versao: z.string().trim().max(40),
});
/**
 * A vez do agente, a cada poucos segundos: o sinal de vida, a impressora que ele vê (ou não) e o estado
 * dela. A resposta é o trabalho que ele deve imprimir, se houver.
 */
export const vezDoAgenteSchema = z.object({
    impressora: impressoraNaRedeSchema.nullable(),
    estado: estadoGravadoDaImpressoraSchema.nullable(),
    versao: z.string().trim().max(40),
});
/**
 * Cada passo do trabalho, como o agente pede ao servidor para gravar. São as operações de
 * `RegistroDoTrabalho`, e o servidor as aplica com as mesmas condições de estado da impressão direta.
 */
export const registroDoAgenteSchema = z.discriminatedUnion("operacao", [
    z.object({
        operacao: z.literal("marcarCopia"),
        copia: z.number().int().min(1).max(COPIAS_MAXIMAS),
        primeira: z.boolean(),
    }),
    z.object({ operacao: z.literal("gravarJobId"), jobId: z.number().int().min(0) }),
    z.object({ operacao: z.literal("esperandoAImpressora"), detalhe: z.string().max(500) }),
    z.object({
        operacao: z.literal("encerrar"),
        de: z.array(z.enum(ESTADOS_DO_TRABALHO_DE_IMPRESSAO)).min(1).max(2),
        para: z.enum(["concluido", "falhou"]),
        detalhe: z.string().max(1000).nullable(),
    }),
    z.object({ operacao: z.literal("detalheDoCancelamento"), detalhe: z.string().max(500) }),
]);
/** Os estados do trabalho em português, para a tela. */
export const ROTULO_DO_ESTADO_DO_TRABALHO = {
    aguardando: "Aguardando",
    imprimindo: "Imprimindo",
    concluido: "Concluído",
    falhou: "Falhou",
    cancelado: "Cancelado",
};
//# sourceMappingURL=impressao.js.map